import React, { useState } from 'react';
import { Check, Star, Zap, Trophy, Crown, ExternalLink } from 'lucide-react';
import { netPlans, coachingPlans, netImages } from '../data/mockData';

const PricingSection = () => {
  const [activeTab, setActiveTab] = useState('nets');

  return (
    <section id="pricing" className="bg-gray-50 py-12 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black tracking-[0.05em] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Training Plans
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Choose the perfect plan to elevate your cricket game. From casual practice to professional coaching.
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex justify-center gap-2 sm:gap-4 mb-8 sm:mb-12">
          <button
            onClick={() => setActiveTab('nets')}
            className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
              activeTab === 'nets' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <Zap className="inline mr-1 sm:mr-2" size={16} />
            Net Plans
          </button>
          <button
            onClick={() => setActiveTab('coaching')}
            className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
              activeTab === 'coaching' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <Trophy className="inline mr-1 sm:mr-2" size={16} />
            Coaching
          </button>
        </div>

        {/* Net Plans */}
        {activeTab === 'nets' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {netPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border-2 ${
                  plan.popular ? 'border-yellow-400' : 'border-transparent'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-yellow-400 text-black text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                      <Star size={12} fill="currentColor" /> MOST POPULAR
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                    plan.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                    plan.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                    plan.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                    plan.color === 'gold' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {plan.type}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.name}</h3>
                  <div className="text-2xl font-bold text-black">{plan.price}</div>
                  <p className="text-gray-500 text-sm">{plan.period}</p>
                </div>

                <p className="text-gray-600 text-sm text-center mb-4 min-h-[40px]">{plan.description}</p>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                  plan.popular 
                    ? 'bg-black text-white hover:bg-gray-800' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Coaching Plans */}
        {activeTab === 'coaching' && (
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {coachingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border-2 ${
                  plan.popular ? 'border-green-400' : 'border-transparent'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-green-400 text-black text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                      <Crown size={12} /> RECOMMENDED
                    </span>
                  </div>
                )}

                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                      plan.color === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {plan.type}
                    </span>
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-black">{plan.price}</div>
                    <p className="text-gray-500 text-sm">{plan.period}</p>
                  </div>
                </div>

                <p className="text-gray-600 mb-6">{plan.description}</p>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                      {feature.includes('Breatheflow') ? (
                        <span>
                          Access to{' '}
                          <a 
                            href="https://breatheflow.live" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            Breatheflow.live <ExternalLink size={14} />
                          </a>{' '}
                          for mental wellness
                        </span>
                      ) : (
                        feature
                      )}
                    </li>
                  ))}
                </ul>

                <button className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  plan.popular 
                    ? 'bg-green-500 text-white hover:bg-green-600' 
                    : 'bg-black text-white hover:bg-gray-800'
                }`}>
                  Enroll Now
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            All plans include access to our state-of-the-art facilities. 
            <a href="#contact" className="text-black font-medium hover:underline ml-1">
              Contact us for custom plans →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
